import { Question } from "../models/RestModels";

/**
 * Utilerias para preguntas de tipo opcion (select / checkbox).
 *
 * Las opciones viven en question.options como un arreglo JSON de textos:
 *   '["azul","verde","rojo"]'
 *
 * Las respuestas siguen guardando TEXTO plano en answer.answer_text:
 *   select   -> el texto elegido            ("verde")
 *   checkbox -> arreglo JSON de los textos marcados ('["chispas","pasas"]'),
 *               '' cuando nada esta marcado.
 *
 * El tipo 'label' es texto informativo entre preguntas: se muestra al
 * capturar pero nunca lleva respuesta.
 *
 * Asi los visores, list-response y la impresion que ya leen answer_text
 * siguen funcionando sin cambios para los tipos viejos.
 */
export class FormOptions
{
	static OPTION_TYPES = ['select','checkbox'];

	static isOptionType(question:Question | null | undefined):boolean
	{
		return !!question && FormOptions.OPTION_TYPES.indexOf(question.type) >= 0;
	}

	static isCheckbox(question:Question | null | undefined):boolean
	{
		return !!question && question.type == 'checkbox';
	}

	//Pregunta informativa: solo se muestra su texto, sin captura ni respuesta.
	static isLabel(question:Question | null | undefined):boolean
	{
		return !!question && question.type == 'label';
	}

	//Parsea question.options (arreglo JSON). Devuelve [] si es vacio/invalido.
	static parseOptions(question:Question | null | undefined):string[]
	{
		let raw = question?.options;

		if( !raw )
			return [];

		try
		{
			let parsed = JSON.parse(raw);

			if( Array.isArray(parsed) )
				return parsed.map((o)=>String(o)).filter((o)=>o !== '');
		}
		catch(e)
		{
			//No es JSON: tolerar texto plano separado por saltos de linea o comas.
			return raw.split(/\r?\n|,/).map((o)=>o.trim()).filter((o)=>o !== '');
		}

		return [];
	}

	//Serializa una lista de textos a question.options; null si queda vacia.
	static serializeOptions(options:string[]):string | null
	{
		let clean = (options || []).map((o)=>String(o).trim()).filter((o)=>o !== '');
		return clean.length ? JSON.stringify(clean) : null;
	}

	//Parsea el answer_text de un checkbox (arreglo JSON con los marcados).
	static parseCheckboxAnswer(answer_text:string | null | undefined):string[]
	{
		if( answer_text == null || !String(answer_text).trim() )
			return [];

		try
		{
			let parsed = JSON.parse(answer_text);

			if( Array.isArray(parsed) )
				return parsed.map((o)=>String(o));
		}
		catch(e)
		{
			//Texto simple: respuesta vieja o capturada sin el widget.
		}

		return [String(answer_text)];
	}

	static encodeCheckboxAnswer(selected:string[]):string
	{
		return selected && selected.length ? JSON.stringify(selected) : '';
	}

	//Valor listo para mostrarse (viewer, list-response, impresion).
	static formatAnswer(question:Question, answer_text:string | null | undefined):string
	{
		if( FormOptions.isCheckbox(question) )
			return FormOptions.parseCheckboxAnswer(answer_text).join(', ');

		return answer_text != null ? String(answer_text) : '';
	}

	//Indica si una pregunta ya esta contestada (para validacion de obligatorias).
	static isAnswered(question:Question, answer_text:string | null | undefined):boolean
	{
		//Los textos informativos nunca requieren respuesta.
		if( FormOptions.isLabel(question) )
			return true;

		if( FormOptions.isCheckbox(question) )
			return FormOptions.parseCheckboxAnswer(answer_text).length > 0;

		return !!(answer_text != null && String(answer_text).trim());
	}
}
